import { observable, makeObservable, runInAction } from 'mobx';
import { get, patch, post } from 'stores/_services/dataService';
import getMaintenanceWindows from '../../helpers/getMaintenanceWindows';

export interface IContact {
    email: string;
    name: string;
    title: string;
    countryCode: string;
    ext: string;
    phone: string;
}

export interface IGeneralInfo {
    maintenanceWindow: string;
    customerNumber: string;
}

export interface ITenantInfo extends IGeneralInfo {
    id?: string;
    billingEmail: string;
    billingName: string;
    billingTitle: string;
    billingCountryCode: string;
    billingExtension: string;
    billingPhone: string;
    technicalEmail: string;
    technicalName: string;
    technicalTitle: string;
    technicalCountryCode: string;
    technicalExtension: string;
    technicalPhone: string;
    mainEmail: string;
    mainName: string;
    mainTitle: string;
    mainCountryCode: string;
    mainExtension: string;
    mainPhone: string;
    tenantLogoBase64: string;
}

export enum ContactType {
    TECHNICAL = 'technical',
    BILLING = 'billing',
    MAIN = 'main'
}

export default class TenantInfoStore {
    id = '';
    maintenanceWindow: string | undefined = '';
    customerNumber: string | undefined = '';
    tenantLogoBase64: string | undefined = '';
    mainContact: IContact = {
        email: '',
        name: '',
        title: '',
        countryCode: '',
        ext: '',
        phone: ''
    };

    technicalContact: IContact = {
        email: '',
        name: '',
        title: '',
        countryCode: '',
        ext: '',
        phone: ''
    };

    billingContact: IContact = {
        email: '',
        name: '',
        title: '',
        countryCode: '',
        ext: '',
        phone: ''
    };

    constructor() {
        makeObservable(this, {
            tenantLogoBase64: observable,
            maintenanceWindow: observable,
            customerNumber: observable,
            mainContact: observable,
            technicalContact: observable,
            billingContact: observable
        });
    }

    async getTenantInfo(): Promise<void> {
        const { data } = await get('tenantinfo');

        if (data?.[0]) {
            this._updateData(data[0]);
        }
    }

    async getClientLogoDownloadUrl(): Promise<{ logo?: string; error?: any }> {
        const { data } = await get('tenantinfo/getClientLogoDownloadUrl');
        if (!data.url) {
            return {};
        }

        const response = await fetch(data.url, { method: 'GET' });
        const blob = await response.blob();

        try {
            const logo = (await this.toBase64(blob)) as string;
            return { logo };
        } catch {
            return {
                error: {
                    title: 'Cannot load logo',
                    message: 'Invalid image file.',
                    type: 'error'
                }
            };
        }
    }

    async toBase64(file: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => {
                    resolve(reader.result);
                };
                img.onerror = (error) => {
                    reject(error);
                };
                img.src = reader.result as string;
            };

            reader.readAsDataURL(file);
        });
    }

    async deleteLogo() {
        await post('tenantInfo/deleteLogo');
    }

    async editContactInfo(data: IContact, type: ContactType): Promise<void> {
        const mappedData = {
            [`${type}Email`]: data.email,
            [`${type}Name`]: data.name,
            [`${type}Title`]: data.title,
            [`${type}CountryCode`]: data.countryCode,
            [`${type}Phone`]: data.phone,
            [`${type}Ext`]: data.ext
        };

        return this._saveTenantInfo(mappedData);
    }

    async editGeneralInfo(data: Partial<IGeneralInfo>): Promise<void> {
        const mappedData = { ...data };

        if (mappedData.maintenanceWindow) {
            const windows = getMaintenanceWindows();

            for (let i = 0; i < windows.length; i++) {
                if (windows[i].local === mappedData.maintenanceWindow) {
                    mappedData.maintenanceWindow = windows[i].utc;
                    break;
                }
            }
        }

        return this._saveTenantInfo(mappedData);
    }

    private async _saveTenantInfo(payload: Partial<ITenantInfo>): Promise<void> {
        let res;

        if (this.id) {
            res = await patch('tenantinfo', { id: this.id, ...payload });
        } else {
            res = await post('tenantinfo', payload);
        }

        const { data } = res;

        this._updateData(data);
    }

    private _updateData(data: ITenantInfo): void {
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.id = data.id || '';
            this.maintenanceWindow = this._getLocalWindow(data.maintenanceWindow);
            this.customerNumber = data.customerNumber;

            this.billingContact = {
                email: data.billingEmail,
                name: data.billingName,
                title: data.billingTitle,
                countryCode: data.billingCountryCode,
                phone: data.billingPhone,
                ext: data.billingExtension
            };

            this.technicalContact = {
                email: data.technicalEmail,
                name: data.technicalName,
                title: data.technicalTitle,
                countryCode: data.technicalCountryCode,
                phone: data.technicalPhone,
                ext: data.technicalExtension
            };

            this.mainContact = {
                email: data.mainEmail,
                name: data.mainName,
                title: data.mainTitle,
                countryCode: data.mainCountryCode,
                phone: data.mainPhone,
                ext: data.mainExtension
            };
        });
    }

    private _getLocalWindow(window: string): string | undefined {
        const windows = getMaintenanceWindows();
        for (let i = 0; i < windows.length; i++) {
            if (windows[i].utc === window) {
                return windows[i].local;
            }
        }
    }
}
